import React from 'react';

import type { IntlShape } from 'react-intl';

import type { ExtensionManifest } from '@atlaskit/editor-common/extensions';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import {
	AGENT_MANAGED_EXTENSION_KEY,
	AGENT_MANAGED_EXTENSION_TYPE_DEFAULT,
	type AgentManagedExtensionPlugin,
} from '../common/agentManagedExtensionPluginType';

import { getContextualToolbars } from './contextual-toolbar';
import { getAgentManagedExtensionIconLoader } from './icon';
import { getQuickInsertModules } from './quick-insert';
import { AgentManagedBodiedExtensionRenderer } from './render-bodied-extension';

const TITLE = 'Agent-managed block';
const DESCRIPTION =
	'A block whose contents are kept up-to-date by a Rovo agent using pre-configured criteria';

/**
 * `ExtensionManifest` for the agent-managed bodied extension. The body
 * is intentionally read-only and populated programmatically by the Rovo agent.
 */
export const getAgentManagedExtensionManifest = ({
	editorAPI,
	extensionType = AGENT_MANAGED_EXTENSION_TYPE_DEFAULT,
	intl,
	providerFactory,
}: {
	editorAPI?: PublicPluginAPI<[AgentManagedExtensionPlugin]>;
	extensionType?: string;
	intl: IntlShape;
	providerFactory?: ProviderFactory;
}): ExtensionManifest => ({
	title: TITLE,
	description: DESCRIPTION,
	type: extensionType,
	key: AGENT_MANAGED_EXTENSION_KEY,
	icons: {
		'48': getAgentManagedExtensionIconLoader(TITLE, 'medium'),
	},
	modules: {
		quickInsert: getQuickInsertModules({ extensionType, intl }),
		contextualToolbars: getContextualToolbars({ editorAPI, extensionType, intl }),
		nodes: {
			default: {
				type: 'bodiedExtension',
				render: () =>
					Promise.resolve(({ node }) => (
						<AgentManagedBodiedExtensionRenderer node={node} providerFactory={providerFactory} readOnly />
					)),
			},
		},
	},
});
